/**
 * Referral Store
 * Remembers the `?ref=` code from a referral link until the visitor signs up.
 *
 * Kept in localStorage (not sessionStorage or component state) so the code
 * survives navigating between pages, reloads and coming back in a new tab.
 * The backend only applies it when it actually creates a user, so sending it
 * on a social login of an existing account is harmless.
 */

const STORAGE_KEY = "referral_code";
/** A referral link stops counting after this long, so a stale code on a
 * shared computer can't credit someone for an unrelated signup. */
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;
/** Guard against junk in the URL ending up in every auth request. */
const MAX_CODE_LENGTH = 64;

interface StoredReferral {
  code: string;
  savedAt: number;
}

/** Saves the code, replacing any earlier one (the latest link wins). */
export function saveReferralCode(code: string): void {
  const trimmed = code.trim();
  if (!trimmed || trimmed.length > MAX_CODE_LENGTH) return;

  const value: StoredReferral = { code: trimmed, savedAt: Date.now() };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Storage blocked (private mode, disabled cookies) - referral is best-effort.
  }
}

/** Returns the stored code, or null if there is none or it has expired. */
export function getReferralCode(): string | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const stored = JSON.parse(raw) as Partial<StoredReferral>;
    if (typeof stored.code !== "string" || typeof stored.savedAt !== "number") {
      clearReferralCode();
      return null;
    }
    if (Date.now() - stored.savedAt > MAX_AGE_MS) {
      clearReferralCode();
      return null;
    }
    return stored.code;
  } catch {
    return null;
  }
}

/** Forgets the code - called once the visitor has signed up or logged in. */
export function clearReferralCode(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to clear if storage is unavailable.
  }
}

/** Saves the `ref` query parameter of a URL search string, if present. */
export function captureReferralCode(search: string): void {
  const ref = new URLSearchParams(search).get("ref");
  if (ref) saveReferralCode(ref);
}

/** Appends `?ref=<code>` to an endpoint when a referral code is stored. */
export function withReferralParam(endpoint: string): string {
  const code = getReferralCode();
  if (!code) return endpoint;
  const separator = endpoint.includes("?") ? "&" : "?";
  return `${endpoint}${separator}ref=${encodeURIComponent(code)}`;
}
